import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../components/ui/dialog";

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");

  const filters = [
    { id: "all", label: "All" },
    { id: "facial", label: "Facial Balancing" },
    { id: "lips", label: "Lip Enhancement" },
    { id: "skin", label: "Skin Rejuvenation" },
  ];

  const galleryItems = [
    {
      id: 1,
      category: "facial",
      title: "Cheek & Jawline Enhancement",
      description: "Natural volume restoration with dermal fillers",
      image: "https://images.unsplash.com/photo-1727386245768-a284a5fc1548?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    },
    {
      id: 2,
      category: "lips",
      title: "Subtle Lip Enhancement",
      description: "Balanced lip augmentation for natural fullness",
      image: "https://images.unsplash.com/photo-1727386245813-c70d8883995e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    },
    {
      id: 3,
      category: "skin",
      title: "Skin Texture Improvement",
      description: "Microneedling series results",
      image: "https://images.unsplash.com/photo-1621770522934-bfcc3b284df7?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    },
    {
      id: 4,
      category: "facial",
      title: "Full Facial Harmonization",
      description: "Comprehensive facial balancing treatment",
      image: "https://images.unsplash.com/photo-1691934338633-178293da4a99?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    },
    {
      id: 5,
      category: "skin",
      title: "Radiance Restoration",
      description: "Chemical peel and laser combination",
      image: "https://images.unsplash.com/photo-1677568554453-ae5baf28da6c?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    },
    {
      id: 6,
      category: "facial",
      title: "Chin & Profile Refinement",
      description: "Non-surgical chin augmentation",
      image: "https://images.pexels.com/photos/30853594/pexels-photo-30853594.jpeg?auto=compress&cs=tinysrgb&w=600",
    },
    {
      id: 7,
      category: "lips",
      title: "Natural Lip Definition",
      description: "Lip border and volume enhancement",
      image: "https://images.unsplash.com/photo-1550572017-4b7a301b9d81?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    },
    {
      id: 8,
      category: "skin",
      title: "Anti-Aging Treatment",
      description: "Neurotoxin and filler combination",
      image: "https://images.unsplash.com/photo-1593049258487-894b6252f629?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    },
  ];

  const filteredItems =
    activeFilter === "all"
      ? galleryItems
      : galleryItems.filter((item) => item.category === activeFilter);

  return (
    <div data-testid="gallery-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.3em] text-taupe mb-4">
              Transformations
            </p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.9] text-matte">
              Before & After
              <br />
              <span className="italic">Gallery</span>
            </h1>
            <p className="mt-8 text-base md:text-lg font-light text-matte/70 leading-relaxed max-w-xl">
              Witness the artistry of our work. Each transformation showcases our 
              commitment to natural-looking results that enhance individual beauty.
            </p>
          </div>
        </div>
      </section>

      {/* Filter & Gallery */}
      <section className="py-24 md:py-32 bg-smoke">
        <div className="container mx-auto px-4 md:px-8">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-12 md:mb-16">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                data-testid={`filter-${filter.id}`}
                className={`text-xs uppercase tracking-[0.15em] px-6 py-3 border transition-all duration-300 ${
                  activeFilter === filter.id
                    ? "bg-matte text-smoke border-matte"
                    : "bg-transparent text-matte border-matte/20 hover:border-taupe"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                data-testid={`gallery-item-${item.id}`}
                className="group cursor-pointer"
                onClick={() => setSelectedImage(item)}
              >
                <div className="image-reveal aspect-[4/5] overflow-hidden mb-4">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-serif text-lg text-matte group-hover:text-taupe transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="text-sm font-light text-matte/50 mt-1">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-16">
              <p className="text-matte/50">No results found for this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl bg-smoke border-none p-0 overflow-hidden">
          <DialogTitle className="sr-only">
            {selectedImage?.title || "Gallery Image"}
          </DialogTitle>
          {selectedImage && (
            <div className="relative">
              <button
                onClick={() => setSelectedImage(null)}
                data-testid="modal-close-btn"
                className="absolute top-4 right-4 z-10 p-2 bg-smoke/80 rounded-full hover:bg-smoke transition-colors"
              >
                <X size={20} weight="light" className="text-matte" />
              </button>
              <img
                src={selectedImage.image}
                alt={selectedImage.title}
                className="w-full h-auto"
              />
              <div className="p-6 md:p-8 bg-smoke">
                <h3 className="font-serif text-2xl text-matte">
                  {selectedImage.title}
                </h3>
                <p className="text-sm font-light text-matte/60 mt-2">
                  {selectedImage.description}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Testimonial Section */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="testimonial-card">
              <p className="font-serif text-2xl md:text-3xl font-light text-matte leading-relaxed italic">
                The results exceeded my expectations. Dr. Chambers understood exactly 
                what I wanted subtle enhancement that still looked like me, just refreshed.
              </p>
              <div className="mt-8">
                <p className="text-sm font-medium text-matte">Sarah M.</p>
                <p className="text-xs text-taupe uppercase tracking-wider mt-1">
                  Facial Balancing Patient
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-smoke">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-matte">
            Start Your Transformation
          </h2>
          <p className="mt-6 text-base font-light text-matte/60 max-w-lg mx-auto">
            Schedule your consultation and discover what personalized aesthetic 
            care can do for you.
          </p>
          <Link
            to="/contact"
            data-testid="gallery-cta-btn"
            className="btn-primary inline-flex items-center gap-3 mt-10"
          >
            Book Consultation
            <ArrowRight size={16} weight="light" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
